import { Loader2, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ErrorAlert from '../components/ErrorAlert';
import { ScreenHeader } from '../components/layout/ScreenHeader';
import Modal from '../components/Modal';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import {
  type VideoKind,
  type VideoListItem,
  useDeleteVideoMutation,
  useLazyPlaybackQuery,
  useListVideosQuery,
  useSyncVideosFromDiskMutation,
} from '../store/coachApi';

type PlaybackState = {
  href?: string;
  error?: string;
  loading?: boolean;
};

const KIND_KEY: Record<VideoKind, string> = {
  library: 'Текущий в библиотеке',
  active: 'Активный план',
  archive: 'Архив',
  unattached: 'Не привязан',
};

const VideosRoute = () => {
  const { t, i18n } = useTranslation();
  const navigate = useTransitionNavigate();
  const location = useLocation();
  const locationState = location.state as { from?: string; planFormDraftKey?: string } | null;
  const from = locationState?.from;
  const backTo = from && from !== '/videos' ? from : '/profile';

  const { data: videos, isLoading, error, refetch } = useListVideosQuery();
  const [syncVideosFromDisk] = useSyncVideosFromDiskMutation();
  const [deleteVideo] = useDeleteVideoMutation();
  const [fetchPlayback] = useLazyPlaybackQuery();
  const [playbackById, setPlaybackById] = useState<Record<string, PlaybackState>>({});
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    void syncVideosFromDisk();
  }, [syncVideosFromDisk]);

  const items = videos ?? [];

  const formatDate = (createdAt: string) =>
    new Date(createdAt).toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  const watchTechnique = async (videoId: string) => {
    setPlaybackById((prev) => ({ ...prev, [videoId]: { loading: true } }));
    try {
      const result = await fetchPlayback(videoId).unwrap();
      setPlaybackById((prev) => ({ ...prev, [videoId]: { href: result.href } }));
    } catch {
      setPlaybackById((prev) => ({
        ...prev,
        [videoId]: { error: t('Ролик временно недоступен') },
      }));
    }
  };

  const removeVideo = async (id: string) => {
    setDeletingId(id);
    setActionError('');
    try {
      await deleteVideo(id).unwrap();
      setPlaybackById((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setConfirmId(null);
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Не удалось удалить ролик'));
    } finally {
      setDeletingId(null);
    }
  };

  const askDelete = (video: VideoListItem) => {
    if (video.kind === 'library' || video.kind === 'active') {
      setConfirmId(video.id);
      return;
    }
    void removeVideo(video.id);
  };

  return (
    <div className="page-container max-w-lg">
      <ScreenHeader
        title={t('Ролики')}
        subtitle={
          videos ? (
            <span className="text-base font-bold text-slate-800">{videos.length} / 60</span>
          ) : undefined
        }
        onBack={() =>
          navigate(
            backTo,
            locationState?.planFormDraftKey
              ? { state: { planFormDraftKey: locationState.planFormDraftKey } }
              : undefined,
          )
        }
        backLabel={t('Назад')}
      />

      {error && (
        <ErrorAlert
          message={getApiErrorMessage(error, 'Не удалось загрузить ролики')}
          onRetry={() => void refetch()}
        />
      )}
      {actionError && <ErrorAlert message={actionError} onDismiss={() => setActionError('')} />}

      {isLoading && !videos ? (
        <div className="flex justify-center py-20">
          <div className="spinner h-8 w-8" />
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state p-12">
          <p className="font-medium">{t('Нет роликов')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((video) => {
            const playback = playbackById[video.id];
            return (
              <div key={video.id} className="card p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 break-words">
                      {video.label || t(KIND_KEY[video.kind])}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatDate(video.createdAt)} · {t(KIND_KEY[video.kind])}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => askDelete(video)}
                    disabled={deletingId === video.id}
                    className="btn-ghost shrink-0 min-h-11 min-w-11 text-slate-400 hover:text-red-500"
                    aria-label={t('Удалить')}
                  >
                    {deletingId === video.id ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Trash2 size={18} />
                    )}
                  </button>
                </div>
                <button
                  type="button"
                  className="btn-secondary w-full min-h-12"
                  onClick={() => void watchTechnique(video.id)}
                >
                  {playback?.loading && <Loader2 size={16} className="animate-spin" />}
                  {t('Смотреть технику')}
                </button>
                {playback?.error && (
                  <p className="text-sm font-medium text-red-600">{playback.error}</p>
                )}
                {playback?.href && (
                  <video controls src={playback.href} className="w-full rounded-2xl bg-black" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {confirmId && (
        <Modal onClose={() => setConfirmId(null)} panelClassName="sm:max-w-md">
          <div className="modal-header flex justify-between items-start gap-4">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight pr-2">
              {t('Удалить')}
            </h2>
            <button
              type="button"
              onClick={() => setConfirmId(null)}
              className="btn-ghost shrink-0 -mr-1 -mt-1"
              aria-label={t('Закрыть')}
            >
              <X size={22} />
            </button>
          </div>
          <p className="text-sm text-slate-600">
            {t('Вы уверены, что хотите удалить этот ролик?')}
          </p>
          <div className="modal-actions flex flex-col gap-3 mt-6">
            <button
              type="button"
              onClick={() => void removeVideo(confirmId)}
              disabled={deletingId === confirmId}
              className="btn-primary w-full min-h-12"
            >
              {deletingId === confirmId && <Loader2 size={16} className="animate-spin" />}
              {t('Удалить')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmId(null)}
              className="btn-secondary w-full min-h-12"
            >
              {t('Отмена')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default VideosRoute;
